import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Radio, Globe, MapPin, Users, Wifi } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mercator projection helpers
const projectLat = (lat) => {
  const latRad = (lat * Math.PI) / 180;
  const mercN = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  return 50 - (mercN / Math.PI) * 50; // 0-100 range, roughly
};
const projectLon = (lon) => ((lon + 180) / 360) * 100;

// Simplified world map outline paths (as SVG polylines)
const WorldMapSVG = ({ listeners }) => {
  const [hoveredCity, setHoveredCity] = useState(null);

  return (
    <svg viewBox="0 0 1000 500" className="w-full h-full" style={{ background: "transparent" }}>
      {/* Grid lines */}
      {[...Array(19)].map((_, i) => (
        <line key={`h${i}`} x1="0" y1={i * 27.78} x2="1000" y2={i * 27.78} stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
      ))}
      {[...Array(37)].map((_, i) => (
        <line key={`v${i}`} x1={i * 27.78} y1="0" x2={i * 27.78} y2="500" stroke="rgba(255,255,255,0.03)" strokeWidth="0.5" />
      ))}

      {/* Simplified continent outlines */}
      {/* North America */}
      <path d="M120,80 L180,60 L220,65 L260,70 L280,80 L270,100 L260,120 L240,140 L220,150 L200,160 L180,180 L170,200 L160,210 L140,200 L120,190 L110,170 L100,150 L90,130 L100,110 L110,90 Z" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
      {/* South America */}
      <path d="M220,240 L240,230 L260,240 L270,260 L280,290 L290,320 L285,350 L270,380 L250,400 L230,390 L220,370 L210,340 L200,310 L205,280 L210,260 Z" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
      {/* Europe */}
      <path d="M450,80 L470,70 L500,75 L520,80 L530,90 L540,100 L530,110 L520,120 L500,130 L480,130 L460,120 L450,110 L445,95 Z" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
      {/* Africa */}
      <path d="M460,150 L490,140 L520,145 L540,160 L550,190 L560,220 L555,260 L540,290 L520,310 L500,320 L480,310 L460,290 L450,260 L445,230 L440,200 L445,170 Z" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
      {/* Asia */}
      <path d="M560,60 L600,50 L650,55 L700,60 L750,70 L780,80 L800,100 L790,120 L770,140 L740,150 L700,160 L660,155 L620,140 L580,130 L560,110 L550,90 Z" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
      {/* Australia */}
      <path d="M750,300 L790,290 L830,295 L850,310 L845,330 L830,345 L800,350 L770,340 L755,320 Z" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>

      {/* Connection lines from Detroit to listener cities */}
      {listeners.map((l, i) => {
        if (l.is_home) return null;
        const x1 = projectLon(-83.0458) * 10;
        const y1 = projectLat(42.3314) * 5;
        const x2 = projectLon(l.lon) * 10;
        const y2 = projectLat(l.lat) * 5;
        return (
          <line key={`line-${i}`} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke="rgba(255,0,0,0.08)" strokeWidth="0.5" strokeDasharray="4,4">
            <animate attributeName="stroke-dashoffset" from="8" to="0" dur="2s" repeatCount="indefinite" />
          </line>
        );
      })}

      {/* Listener dots */}
      {listeners.map((l, i) => {
        const cx = projectLon(l.lon) * 10;
        const cy = projectLat(l.lat) * 5;
        const r = l.is_home ? 8 : Math.max(3, Math.min(6, l.listeners / 2));
        
        return (
          <g key={i}
            onMouseEnter={() => setHoveredCity(i)}
            onMouseLeave={() => setHoveredCity(null)}
            style={{ cursor: "pointer" }}
          >
            {/* Pulse ring */}
            <circle cx={cx} cy={cy} r={r * 3} fill="none" stroke={l.is_home ? "rgba(255,0,0,0.3)" : "rgba(255,0,0,0.15)"} strokeWidth="1">
              <animate attributeName="r" from={r} to={r * 3} dur={l.is_home ? "1.5s" : "2.5s"} repeatCount="indefinite" />
              <animate attributeName="opacity" from="0.6" to="0" dur={l.is_home ? "1.5s" : "2.5s"} repeatCount="indefinite" />
            </circle>
            
            {/* Dot */}
            <circle cx={cx} cy={cy} r={r} fill={l.is_home ? "#ff0000" : "rgba(255,60,60,0.8)"} stroke="rgba(255,255,255,0.3)" strokeWidth="0.5">
              {!l.is_home && <animate attributeName="opacity" values="0.5;1;0.5" dur="3s" begin={`${i * 0.2}s`} repeatCount="indefinite" />}
            </circle>

            {/* Home marker */}
            {l.is_home && (
              <>
                <circle cx={cx} cy={cy} r={r + 3} fill="none" stroke="#ff0000" strokeWidth="1.5" opacity="0.6">
                  <animate attributeName="r" from={r + 2} to={r + 10} dur="2s" repeatCount="indefinite" />
                  <animate attributeName="opacity" from="0.6" to="0" dur="2s" repeatCount="indefinite" />
                </circle>
                <text x={cx} y={cy - r - 6} textAnchor="middle" fill="#ff0000" fontSize="8" fontFamily="monospace" fontWeight="bold">HQ</text>
              </>
            )}

            {/* Tooltip on hover */}
            {hoveredCity === i && (
              <g>
                <rect x={cx + 10} y={cy - 25} width={Math.max(80, l.city.length * 7 + 40)} height="35" rx="3" fill="rgba(0,0,0,0.9)" stroke="rgba(255,0,0,0.4)" strokeWidth="1" />
                <text x={cx + 16} y={cy - 10} fill="white" fontSize="9" fontFamily="monospace" fontWeight="bold">
                  {l.city}{l.state ? `, ${l.state}` : ""}
                </text>
                <text x={cx + 16} y={cy + 2} fill="rgba(255,100,100,1)" fontSize="8" fontFamily="monospace">
                  {l.listeners} listener{l.listeners !== 1 ? "s" : ""}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
};

const ListenerMapPage = () => {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMapData();
    const interval = setInterval(fetchMapData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchMapData = async () => {
    try {
      const res = await axios.get(`${API}/listeners/map`);
      setMapData(res.data);
    } catch (e) {
      console.error("Map data fetch failed");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
            <Globe className="w-12 h-12 text-neon-red mx-auto" />
          </motion.div>
          <p className="font-mono text-sm text-chrome-mid mt-4">LOADING LISTENER MAP...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-16 px-4 md:px-6" data-testid="listener-map-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-4 mb-6">
          <div className="red-line h-12" />
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-neon-red mb-1">HOT LIVE 95 DETROIT</p>
            <h1 className="font-display text-3xl md:text-4xl chrome-text">LIVE LISTENER MAP</h1>
          </div>
        </motion.div>

        {mapData && (
          <>
            {/* Stats Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
            >
              <div className="bg-surface/80 border border-neon-red/30 p-4 text-center" data-testid="stat-total">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Wifi className="w-4 h-4 text-neon-red" />
                  <span className="font-mono text-xs text-chrome-dark">TOTAL LISTENERS</span>
                </div>
                <p className="font-display text-3xl text-white">{mapData.total_listeners}</p>
              </div>
              <div className="bg-surface/80 border border-white/10 p-4 text-center" data-testid="stat-detroit">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-neon-red" />
                  <span className="font-mono text-xs text-chrome-dark">DETROIT</span>
                </div>
                <p className="font-display text-3xl text-neon-red">{mapData.regions.detroit}</p>
              </div>
              <div className="bg-surface/80 border border-white/10 p-4 text-center" data-testid="stat-us">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-chrome-mid" />
                  <span className="font-mono text-xs text-chrome-dark">REST OF US</span>
                </div>
                <p className="font-display text-3xl text-white">{mapData.regions.us}</p>
              </div>
              <div className="bg-surface/80 border border-white/10 p-4 text-center" data-testid="stat-intl">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Globe className="w-4 h-4 text-chrome-mid" />
                  <span className="font-mono text-xs text-chrome-dark">INTERNATIONAL</span>
                </div>
                <p className="font-display text-3xl text-white">{mapData.regions.international}</p>
              </div>
            </motion.div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="bg-surface/60 border border-white/10 p-2 md:p-4 relative overflow-hidden"
              data-testid="listener-map"
            >
              {/* Glow effect behind map */}
              <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-neon-red/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
              
              <div className="relative" style={{ aspectRatio: "2/1" }}>
                <WorldMapSVG listeners={mapData.listeners} />
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-4 pb-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500 relative">
                    <div className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-30" />
                  </div>
                  <span className="text-xs text-chrome-mid font-mono">HQ (Detroit)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                  <span className="text-xs text-chrome-mid font-mono">Active Listeners</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-[1px] border-t border-dashed border-red-500/30" />
                  <span className="text-xs text-chrome-mid font-mono">Connection</span>
                </div>
              </div>
            </motion.div>

            {/* Top Cities Table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-surface/80 border border-white/10 p-6 mt-6"
              data-testid="top-cities"
            >
              <p className="font-mono text-xs tracking-widest text-neon-red mb-4">TOP LISTENING CITIES</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[...mapData.listeners]
                  .sort((a, b) => b.listeners - a.listeners)
                  .slice(0, 12)
                  .map((l, i) => {
                    const maxListeners = mapData.listeners[0]?.listeners || 1;
                    const barWidth = (l.listeners / Math.max(...mapData.listeners.map(x => x.listeners))) * 100;
                    return (
                      <div key={i} className="flex items-center gap-3 p-2">
                        <span className="font-mono text-xs text-chrome-dark w-5 text-right">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-white truncate flex items-center gap-1.5">
                              {l.is_home && <MapPin className="w-3 h-3 text-neon-red flex-shrink-0" />}
                              {l.city}{l.state ? `, ${l.state}` : ""}
                            </span>
                            <span className="text-xs text-chrome-mid ml-2 flex-shrink-0">{l.listeners}</span>
                          </div>
                          <div className="w-full h-1 bg-white/5 rounded-full">
                            <div
                              className="h-full rounded-full transition-all duration-700"
                              style={{
                                width: `${barWidth}%`,
                                background: l.is_home ? "#ff0000" : "rgba(255,60,60,0.6)"
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </motion.div>

            {/* Note */}
            <p className="text-center text-chrome-dark text-xs mt-4 font-mono">
              <Radio className="w-3 h-3 inline mr-1" />
              Listener data refreshes every 30 seconds • Broadcasting from Detroit, MI
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ListenerMapPage;
